import React from 'react';

export function Card(props: { title?: string; children: React.ReactNode; className?: string }) {
  return (
    <div className={`rounded-xl border border-neutral-800 bg-neutral-900/70 p-4 ${props.className ?? ''}`}>
      {props.title && <h3 className="mb-3 text-sm font-semibold text-neutral-300">{props.title}</h3>}
      {props.children}
    </div>
  );
}

export function Button(props: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'ghost' | 'danger' }) {
  const { variant = 'primary', className, ...rest } = props;
  const styles = {
    primary: 'bg-brand-500 hover:bg-brand-600 text-white',
    ghost: 'bg-neutral-800 hover:bg-neutral-700 text-neutral-200',
    danger: 'bg-red-600/90 hover:bg-red-600 text-white',
  }[variant];
  return (
    <button
      {...rest}
      className={`rounded-lg px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${styles} ${className ?? ''}`}
    />
  );
}

export function Field(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium text-neutral-400">{props.label}</span>
      <input
        type={props.type ?? 'text'}
        value={props.value}
        placeholder={props.placeholder}
        onChange={(e) => props.onChange(e.target.value)}
        className="w-full rounded-lg border border-neutral-700 bg-neutral-900 px-3 py-2 text-sm text-neutral-100 outline-none focus:border-brand-400"
      />
    </label>
  );
}

const STATUS_COLORS: Record<string, string> = {
  HEALTHY: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  BUSY: 'bg-sky-500/15 text-sky-400 border-sky-500/30',
  RATE_LIMITED: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  QUOTA_EXHAUSTED: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  AUTH_REQUIRED: 'bg-red-500/15 text-red-400 border-red-500/30',
  OFFLINE: 'bg-neutral-500/15 text-neutral-400 border-neutral-500/30',
  MISCONFIGURED: 'bg-red-500/15 text-red-400 border-red-500/30',
  NOT_INSTALLED: 'bg-neutral-500/15 text-neutral-400 border-neutral-600/40',
  DISABLED: 'bg-neutral-500/15 text-neutral-500 border-neutral-600/40',
  CONNECTED: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  INVALID: 'bg-red-500/15 text-red-400 border-red-500/30',
  EXPIRED: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  DISCONNECTED: 'bg-neutral-500/15 text-neutral-400 border-neutral-600/40',
  ACTIVE: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/30',
  PAUSED: 'bg-amber-500/15 text-amber-400 border-amber-500/30',
  ARCHIVED: 'bg-neutral-500/15 text-neutral-500 border-neutral-600/40',
};

export function Badge(props: { status: string; label?: string }) {
  const color = STATUS_COLORS[props.status] ?? 'bg-neutral-500/15 text-neutral-400 border-neutral-600/40';
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${color}`}>
      {props.label ?? props.status.replaceAll('_', ' ')}
    </span>
  );
}

export function EmptyState(props: { title: string; hint: string; action?: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-neutral-800 py-16 text-center">
      <p className="text-sm font-medium text-neutral-300">{props.title}</p>
      <p className="mt-1 max-w-md text-xs text-neutral-500">{props.hint}</p>
      {props.action && <div className="mt-4">{props.action}</div>}
    </div>
  );
}

export function ErrorNote(props: { message: string }) {
  return (
    <div className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-300">
      {props.message}
    </div>
  );
}
